import { useEffect, useState } from 'react'
import { differenceInSeconds, parseISO } from 'date-fns'
import i18n from '@/i18n/config'
import { getAppLanguage } from '@/lib/locale'

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null
  try {
    const parsed = parseISO(value)
    if (isNaN(parsed.getTime())) return null
    return parsed
  } catch {
    return null
  }
}

export function formatCompactRelativeTime(
  value: string | null | undefined,
  now: Date = new Date(),
): string {
  const date = parseDate(value)
  if (!date) return ''

  const diffSec = differenceInSeconds(now, date)
  const lang = getAppLanguage()

  if (diffSec < 0) {
    // Future date — treat as just now (clock skew tolerance)
    return i18n.t('notifications.page.relativeTime.justNow', { defaultValue: 'Just now' })
  }
  if (diffSec < 60) {
    return i18n.t('notifications.page.relativeTime.justNow', { defaultValue: 'Just now' })
  }
  if (diffSec < 3600) {
    const count = Math.floor(diffSec / 60)
    return lang === 'fr'
      ? i18n.t('notifications.page.relativeTime.minutes', { count, defaultValue: `${count} min` })
      : i18n.t('notifications.page.relativeTime.minutes', { count, defaultValue: `${count}m` })
  }
  if (diffSec < 86400) {
    const count = Math.floor(diffSec / 3600)
    return i18n.t('notifications.page.relativeTime.hours', { count, defaultValue: `${count}h` })
  }
  if (diffSec < 604800) {
    const count = Math.floor(diffSec / 86400)
    return lang === 'fr'
      ? i18n.t('notifications.page.relativeTime.days', { count, defaultValue: `${count} j` })
      : i18n.t('notifications.page.relativeTime.days', { count, defaultValue: `${count}d` })
  }

  return date.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', {
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Hook that returns a compact relative time string (e.g. "Just now", "5m", "2h")
 * and auto-refreshes every 30 seconds so the label stays current.
 * Re-computes when the i18n language changes.
 */
export function useRelativeTime(value: string | null | undefined): string {
  const [label, setLabel] = useState(() => formatCompactRelativeTime(value))

  useEffect(() => {
    setLabel(formatCompactRelativeTime(value))

    const interval = setInterval(() => {
      setLabel(formatCompactRelativeTime(value))
    }, 30_000)

    return () => clearInterval(interval)
  }, [value])

  // Re-compute when language changes
  useEffect(() => {
    const handleLanguageChanged = () => {
      setLabel(formatCompactRelativeTime(value))
    }

    i18n.on('languageChanged', handleLanguageChanged)
    return () => {
      i18n.off('languageChanged', handleLanguageChanged)
    }
  }, [value])

  return label
}
